import type { ComponentConfig } from '@measured/puck';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { easeStripe } from '../../lib/motion';

export interface HeroPillarProps {
  pillar: 'CEE' | 'Comparateur' | 'Rénovation' | 'Décret Tertiaire';
  title: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

const PILLAR_META: Record<
  HeroPillarProps['pillar'],
  { code: string; tone: 'brand' | 'accent'; description: string }
> = {
  CEE: { code: 'CEE', tone: 'accent', description: 'Mandataire' },
  Comparateur: { code: 'CRT', tone: 'brand', description: 'Courtage énergie' },
  Rénovation: { code: 'TRV', tone: 'brand', description: 'Travaux' },
  'Décret Tertiaire': { code: 'DT', tone: 'brand', description: 'Conformité' },
};

const DOT_TONE = {
  brand: 'live-dot',
  accent: 'live-dot live-dot-accent',
} as const;

export function HeroPillar({ pillar, title, subtitle, ctaLabel, ctaHref }: HeroPillarProps) {
  const meta = PILLAR_META[pillar];

  return (
    <section className="relative overflow-hidden border-b border-stone-200 bg-paper">
      {/* Background subtil */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="dot-grid-masked absolute inset-0 opacity-70" />
        <div className="spot-glow opacity-70" />
      </div>

      <Container className="relative py-16 md:py-20">
        {/* Eyebrow chip pilier */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: easeStripe }}
          className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur"
        >
          <span className={DOT_TONE[meta.tone]} aria-hidden />
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            {meta.code}
          </span>
          <span className="h-3 w-px bg-stone-200" aria-hidden />
          <span>{meta.description}</span>
        </motion.div>

        {/* Titre */}
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.06, ease: easeStripe }}
          className="h-hero-lg mt-6 max-w-4xl"
        >
          {title}
        </motion.h1>

        {/* Subtitle */}
        {subtitle ? (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.14, ease: easeStripe }}
            className="mt-5 max-w-2xl text-[16px] leading-relaxed text-stone-600"
          >
            {subtitle}
          </motion.p>
        ) : null}

        {/* CTA */}
        {ctaLabel && ctaHref ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22, ease: easeStripe }}
            className="mt-7"
          >
            <Button asChild size="lg" variant="primary" className="group">
              <a href={ctaHref}>
                {ctaLabel}
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
              </a>
            </Button>
          </motion.div>
        ) : null}
      </Container>
    </section>
  );
}

export const HeroPillarConfig: ComponentConfig<HeroPillarProps> = {
  fields: {
    pillar: {
      type: 'select',
      options: [
        { label: 'CEE', value: 'CEE' },
        { label: 'Comparateur', value: 'Comparateur' },
        { label: 'Rénovation', value: 'Rénovation' },
        { label: 'Décret Tertiaire', value: 'Décret Tertiaire' },
      ],
    },
    title: { type: 'textarea' },
    subtitle: { type: 'textarea' },
    ctaLabel: { type: 'text' },
    ctaHref: { type: 'text' },
  },
  defaultProps: {
    pillar: 'CEE',
    title: 'Mandataire CEE pour les artisans RGE et nos propres chantiers.',
    subtitle:
      'En partenariat avec les obligés, nous montons les dossiers CEE des artisans RGE partenaires (ou de nos propres chantiers) et sécurisons la valorisation au meilleur tarif du marché des primes. Aucune avance pour l’artisan.',
    ctaLabel: 'Estimer mes primes',
    ctaHref: '/outils',
  },
  render: HeroPillar,
};
